// Tiny WebAudio "chime" used for in-app notifications.
//
// Design:
//   - No external assets: synthesises a short two-note arpeggio via
//     OscillatorNode so the whole feature is < 1KB and doesn't add a
//     network dependency.
//   - Reads the per-device mute preference from localStorage
//     ("rq_notif_sound_muted" — "1" = muted). Pass { force: true } to
//     bypass the mute check (useful for the Settings preview).
//   - Failures are swallowed silently — sound is non-essential UX.

let _ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
    return _ctx;
  } catch {
    return null;
  }
}

function isMuted() {
  try {
    return localStorage.getItem("rq_notif_sound_muted") === "1";
  } catch {
    return false;
  }
}

/**
 * Play a short two-note chime (E5 → A5).
 * @param {{ force?: boolean }} opts
 */
export function playNotificationSound(opts = {}) {
  if (!opts.force && isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;

  // Some browsers gate AudioContext until the first user gesture — calling
  // resume() after a click guarantees subsequent programmatic plays work.
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch {}
  }

  try {
    const now = ctx.currentTime;
    const notes = [
      { freq: 659.25, start: 0,    dur: 0.15 }, // E5
      { freq: 880.0,  start: 0.12, dur: 0.22 }, // A5
    ];

    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;

      // Quick attack, exponential decay — feels like a soft bell, not a beep.
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
  } catch {
    // ignore — sound is non-essential
  }
}
